import React, { useState, useRef, useEffect, useCallback } from 'react';
import { View, Text, Pressable } from '../lib/react-native-compat';

const url = process.env.REACT_APP_API_URL || 'https://forecastingprodservice.azurewebsites.net/';

/* ── keyframe animations injected once ── */
const styleId = 'ai-chat-animations';
if (typeof document !== 'undefined' && !document.getElementById(styleId)) {
  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    @keyframes fadeSlideUp {
      from { opacity: 0; transform: translateY(12px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes pulse {
      0%, 100% { opacity: .3; }
      50%      { opacity: 1; }
    }
    @keyframes gradientShift {
      0%   { background-position: 0% 50%; }
      50%  { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
    @keyframes fadeIn {
      from { opacity: 0; }
      to   { opacity: 1; }
    }
    .ai-msg-enter {
      animation: fadeSlideUp .3s ease-out both;
    }
    .ai-dot { animation: pulse 1.2s ease-in-out infinite; }
    .ai-dot:nth-child(2) { animation-delay: .2s; }
    .ai-dot:nth-child(3) { animation-delay: .4s; }
    .ai-chat-scroll::-webkit-scrollbar { width: 6px; }
    .ai-chat-scroll::-webkit-scrollbar-track { background: transparent; }
    .ai-chat-scroll::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 3px; }
    .ai-chat-scroll::-webkit-scrollbar-thumb:hover { background: #9ca3af; }
    .ai-stream-cursor::after {
      content: '\\25AE';
      animation: pulse .8s ease-in-out infinite;
      margin-left: 1px;
      color: #2563eb;
    }
    .ai-doc-row { transition: background-color .15s; }
    .ai-doc-row:hover { background-color: #f0f4ff !important; }
    .ai-spinner { animation: spin .8s linear infinite; }
    .ai-panel-enter { animation: fadeIn .25s ease-out both; }
  `;
  document.head.appendChild(style);
}

const fileIcon = (type) => {
  if (!type || type === 'folder') return '\uD83D\uDCC1';
  if (type.includes('pdf')) return '\uD83D\uDCC4';
  if (type.includes('word') || type.includes('document')) return '\uD83D\uDCD8';
  if (type.includes('excel') || type.includes('spreadsheet')) return '\uD83D\uDCCA';
  if (type.includes('powerpoint') || type.includes('presentation')) return '\uD83D\uDCDD';
  if (type.includes('image')) return '\uD83D\uDDBC\uFE0F';
  return '\uD83D\uDCC3';
};

const formatSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / 1048576).toFixed(1) + ' MB';
};

const PROVIDER_COLORS = {
  anthropic: { bg: '#FEF3C7', text: '#92400E', label: 'Claude' },
  azure:     { bg: '#DBEAFE', text: '#1E40AF', label: 'Azure' },
};

const AIChat = () => {
  const [messages, setMessages] = useState([
    { role: 'system', content: 'You are a helpful assistant for a construction forecasting application. When the user asks about documents or SOPs, use the provided SharePoint document context to answer.' }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  const scrollRef = useRef(null);
  const abortRef = useRef(null);

  // Model selection
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState('');
  const [showModelMenu, setShowModelMenu] = useState(false);
  const [activeModel, setActiveModel] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(url + 'api/chatModels');
        const data = await res.json();
        if (data?.models) {
          setModels(data.models);
          setSelectedModel(data.default || data.models[0]?.id);
        }
      } catch { /* keep defaults */ }
    })();
  }, []);

  // Documents panel state
  const [showDocs, setShowDocs] = useState(false);
  const [docs, setDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [docsError, setDocsError] = useState('');
  const [indexing, setIndexing] = useState(false);
  const [indexed, setIndexed] = useState(false);
  const [vectorCount, setVectorCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const scrollToBottom = useCallback(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
    }
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, streamingText, scrollToBottom]);

  // Fetch SharePoint documents
  const fetchDocs = async () => {
    setDocsLoading(true);
    setDocsError('');
    try {
      const res = await fetch(url + 'api/readSharePointDocs');
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setDocs(data.documents || []);
    } catch (err) {
      setDocsError(err.message);
    } finally {
      setDocsLoading(false);
    }
  };

  // Index docs into vector store
  const indexDocs = async () => {
    if (docs.length === 0) return;
    setIndexing(true);
    try {
      const batch = docs.filter(d => d.type !== 'folder').map(d => ({
        id: d.id,
        name: d.name,
        text: [d.name, d.driveName, d.parentFolder, d.description, d.modifiedBy].filter(Boolean).join(' - '),
        webUrl: d.webUrl,
        type: d.type,
        lastModified: d.lastModified,
        modifiedBy: d.modifiedBy,
        driveName: d.driveName,
        parentFolder: d.parentFolder
      }));

      const res = await fetch(url + 'api/vectorStore', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'index', documents: batch })
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setIndexed(true);
      setVectorCount(data.totalStored || 0);
    } catch (err) {
      setDocsError('Indexing failed: ' + err.message);
    } finally {
      setIndexing(false);
    }
  };

  // Search vector store
  const searchDocs = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const res = await fetch(url + 'api/vectorStore', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'search', query: searchQuery.trim(), topK: 8 })
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setSearchResults(data.results || []);
    } catch (err) {
      setDocsError('Search failed: ' + err.message);
    } finally {
      setSearching(false);
    }
  };

  const sendMessage = async () => {
    const trimmed = input.trim();
    if (!trimmed || loading) return;

    const userMessage = { role: 'user', content: trimmed };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInput('');
    setLoading(true);
    setStreamingText('');

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const res = await fetch(url + 'api/openaiChat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: updatedMessages, max_tokens: 4096, temperature: 0.7, model: selectedModel }),
        signal: controller.signal
      });

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let accumulated = '';
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          const trimmedLine = line.trim();
          if (!trimmedLine.startsWith('data: ')) continue;
          const payload = trimmedLine.slice(6);
          if (payload === '[DONE]') break;
          try {
            const parsed = JSON.parse(payload);
            if (parsed.meta?.model) setActiveModel(parsed.meta.model);
            if (parsed.content) {
              accumulated += parsed.content;
              setStreamingText(accumulated);
            }
          } catch { /* skip malformed chunks */ }
        }
      }

      if (accumulated) {
        setMessages(prev => [...prev, { role: 'assistant', content: accumulated }]);
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        setMessages(prev => [...prev, { role: 'assistant', content: 'Error: ' + err.message }]);
      }
    } finally {
      setStreamingText('');
      setLoading(false);
      abortRef.current = null;
    }
  };

  const stopStreaming = () => {
    if (abortRef.current) abortRef.current.abort();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => {
    if (abortRef.current) abortRef.current.abort();
    setMessages([{ role: 'system', content: 'You are a helpful assistant for a construction forecasting application. When the user asks about documents or SOPs, use the provided SharePoint document context to answer.' }]);
    setStreamingText('');
    setLoading(false);
  };

  const visibleMessages = messages.filter(m => m.role !== 'system');
  const displayDocs = searchResults.length > 0 ? searchResults : docs;

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 60px)', width: '100%', boxSizing: 'border-box' }}>
      {/* Main chat area */}
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, padding: '16px 24px', minWidth: 0 }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#1a1a2e' }}>AI Assistant</div>
            <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>
              {activeModel || selectedModel || 'Multi-model'} &middot; Streaming
              {activeModel && models.length > 0 && (() => {
                const spec = models.find(m => m.id === activeModel);
                const ps = spec ? (PROVIDER_COLORS[spec.provider] || PROVIDER_COLORS.azure) : null;
                return ps ? <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 600, padding: '1px 5px', borderRadius: 3, background: ps.bg, color: ps.text }}>{ps.label}</span> : null;
              })()}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {/* Model selector */}
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setShowModelMenu(!showModelMenu)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 5,
                  backgroundColor: '#f5f5f5', border: '1px solid #e5e5e5',
                  borderRadius: 6, padding: '7px 12px', fontSize: 12, cursor: 'pointer',
                  fontFamily: 'inherit', color: '#444', whiteSpace: 'nowrap'
                }}
              >
                ⚙️ {selectedModel || 'Model'} ▾
              </button>
              {showModelMenu && (
                <div style={{
                  position: 'absolute', top: '100%', right: 0, marginTop: 4, zIndex: 20,
                  background: '#fff', border: '1px solid #e5e5e5', borderRadius: 8,
                  boxShadow: '0 6px 20px rgba(0,0,0,.12)', minWidth: 220, overflow: 'hidden'
                }}>
                  {models.map(m => {
                    const ps = PROVIDER_COLORS[m.provider] || PROVIDER_COLORS.azure;
                    return (
                      <button
                        key={m.id}
                        onClick={() => { setSelectedModel(m.id); setShowModelMenu(false); }}
                        style={{
                          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          padding: '9px 14px', border: 'none',
                          background: m.id === selectedModel ? '#f0f4ff' : 'transparent',
                          cursor: 'pointer', fontSize: 13, fontFamily: 'inherit', color: '#333'
                        }}
                      >
                        <span>{m.id}</span>
                        <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 4, background: ps.bg, color: ps.text }}>{ps.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <button
              onClick={() => { setShowDocs(!showDocs); if (!showDocs && docs.length === 0) fetchDocs(); }}
              style={{
                backgroundColor: showDocs ? '#2563eb' : '#f5f5f5',
                color: showDocs ? '#fff' : '#666',
                border: showDocs ? 'none' : '1px solid #e5e5e5',
                borderRadius: 6, padding: '7px 14px', fontSize: 12, cursor: 'pointer',
                fontFamily: 'inherit', whiteSpace: 'nowrap'
              }}
            >
              {showDocs ? 'Hide' : '\uD83D\uDCC2'} Documents
            </button>
            <button
              onClick={clearChat}
              style={{
                backgroundColor: '#f5f5f5', border: '1px solid #e5e5e5',
                borderRadius: 6, padding: '7px 14px', fontSize: 12, cursor: 'pointer',
                fontFamily: 'inherit', color: '#666'
              }}
            >
              Clear Chat
            </button>
          </div>
        </div>

        {/* Messages */}
        <div
          ref={scrollRef}
          className="ai-chat-scroll"
          style={{
            flex: 1, overflowY: 'auto', backgroundColor: '#fafafa',
            borderRadius: 12, border: '1px solid #ebebeb', padding: '20px 20px 8px'
          }}
        >
          {visibleMessages.length === 0 && !loading && (
            <div className="ai-msg-enter" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', paddingTop: 80, paddingBottom: 80 }}>
              <div style={{
                width: 56, height: 56, borderRadius: 28,
                background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
                backgroundSize: '200% 200%', animation: 'gradientShift 3s ease infinite',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: 16, fontSize: 24, color: '#fff'
              }}>AI</div>
              <div style={{ fontSize: 15, color: '#999', textAlign: 'center' }}>Ask me anything about your project</div>
              {!showDocs && (
                <button
                  onClick={() => { setShowDocs(true); if (docs.length === 0) fetchDocs(); }}
                  style={{
                    marginTop: 16, backgroundColor: 'transparent', border: '1px solid #d1d5db',
                    borderRadius: 8, padding: '8px 18px', fontSize: 13, cursor: 'pointer',
                    color: '#2563eb', fontFamily: 'inherit'
                  }}
                >
                  \uD83D\uDCC2 Browse SharePoint Documents
                </button>
              )}
            </div>
          )}

          {visibleMessages.map((msg, i) => (
            <div key={i} className="ai-msg-enter" style={{
              display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
              marginBottom: 8, animationDelay: `${Math.min(i * 0.05, 0.3)}s`
            }}>
              {msg.role === 'assistant' && (
                <div style={{
                  width: 28, height: 28, borderRadius: 14, marginRight: 8, marginTop: 2, flexShrink: 0,
                  background: 'linear-gradient(135deg, #2563eb, #7c3aed)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 11, color: '#fff', fontWeight: 700
                }}>AI</div>
              )}
              <div style={{
                maxWidth: '80%', padding: '8px 14px',
                borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                backgroundColor: msg.role === 'user' ? '#2563eb' : '#fff',
                border: msg.role === 'user' ? 'none' : '1px solid #ebebeb',
                boxShadow: '0 1px 2px rgba(0,0,0,.04)'
              }}>
                <div style={{
                  fontSize: 14, lineHeight: '22px', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                  color: msg.role === 'user' ? '#fff' : '#222'
                }}>{msg.content}</div>
              </div>
            </div>
          ))}

          {loading && streamingText && (
            <div className="ai-msg-enter" style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 8 }}>
              <div style={{
                width: 28, height: 28, borderRadius: 14, marginRight: 8, marginTop: 2, flexShrink: 0,
                background: 'linear-gradient(135deg, #2563eb, #7c3aed)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, color: '#fff', fontWeight: 700
              }}>AI</div>
              <div style={{
                maxWidth: '80%', padding: '8px 14px', borderRadius: '16px 16px 16px 4px',
                backgroundColor: '#fff', border: '1px solid #ebebeb', boxShadow: '0 1px 2px rgba(0,0,0,.04)'
              }}>
                <div className="ai-stream-cursor" style={{ fontSize: 14, lineHeight: '22px', whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: '#222' }}>
                  {streamingText}
                </div>
              </div>
            </div>
          )}

          {loading && !streamingText && (
            <div className="ai-msg-enter" style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 8 }}>
              <div style={{
                width: 28, height: 28, borderRadius: 14, marginRight: 8, marginTop: 2, flexShrink: 0,
                background: 'linear-gradient(135deg, #2563eb, #7c3aed)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, color: '#fff', fontWeight: 700
              }}>AI</div>
              <div style={{
                padding: '12px 18px', borderRadius: '16px 16px 16px 4px',
                backgroundColor: '#fff', border: '1px solid #ebebeb',
                display: 'flex', gap: 5, alignItems: 'center'
              }}>
                <div className="ai-dot" style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: '#2563eb' }} />
                <div className="ai-dot" style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: '#2563eb' }} />
                <div className="ai-dot" style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: '#2563eb' }} />
              </div>
            </div>
          )}
        </div>

        {/* Input area */}
        <div style={{
          display: 'flex', alignItems: 'flex-end', marginTop: 12, flexShrink: 0,
          backgroundColor: '#fff', borderRadius: 12, border: '1px solid #e5e5e5',
          padding: '6px 6px 6px 14px', boxShadow: '0 1px 4px rgba(0,0,0,.06)'
        }}>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a message..."
            rows={1}
            style={{
              flex: 1, border: 'none', outline: 'none', resize: 'none',
              fontSize: 14, fontFamily: 'inherit', padding: '8px 0',
              lineHeight: '22px', backgroundColor: 'transparent',
              maxHeight: 120, overflowY: 'auto'
            }}
            onInput={(e) => {
              e.target.style.height = 'auto';
              e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
            }}
          />
          {loading ? (
            <button
              onClick={stopStreaming}
              style={{
                backgroundColor: '#ef4444', color: '#fff', fontSize: 13, fontWeight: 600,
                border: 'none', borderRadius: 8, padding: '9px 16px', marginLeft: 6,
                cursor: 'pointer', whiteSpace: 'nowrap', lineHeight: '20px', fontFamily: 'inherit'
              }}
            >Stop</button>
          ) : (
            <button
              onClick={sendMessage}
              style={{
                backgroundColor: input.trim() ? '#2563eb' : '#bfdbfe',
                color: '#fff', fontSize: 13, fontWeight: 600,
                border: 'none', borderRadius: 8, padding: '9px 16px', marginLeft: 6,
                cursor: input.trim() ? 'pointer' : 'default',
                transition: 'background-color .2s', whiteSpace: 'nowrap',
                lineHeight: '20px', fontFamily: 'inherit'
              }}
            >Send</button>
          )}
        </div>
      </div>

      {/* Documents side panel */}
      {showDocs && (
        <div className="ai-panel-enter" style={{
          width: 380, flexShrink: 0, borderLeft: '1px solid #ebebeb',
          backgroundColor: '#fff', display: 'flex', flexDirection: 'column',
          height: 'calc(100vh - 60px)', overflow: 'hidden'
        }}>
          {/* Panel header */}
          <div style={{
            padding: '14px 16px', borderBottom: '1px solid #ebebeb',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center'
          }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#1a1a2e' }}>\uD83D\uDCC2 SharePoint Documents</div>
              <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>
                {docs.length > 0 ? `${docs.length} items` : 'Strike Operating Requirements'}
                {indexed && ` \u00B7 ${vectorCount} vectors`}
              </div>
            </div>
            <button
              onClick={() => setShowDocs(false)}
              style={{
                background: 'none', border: 'none', fontSize: 18, cursor: 'pointer',
                color: '#999', padding: '0 4px', lineHeight: 1
              }}
            >&times;</button>
          </div>

          {/* Search + actions */}
          <div style={{ padding: '10px 16px', borderBottom: '1px solid #f0f0f0' }}>
            <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
              <button
                onClick={fetchDocs}
                disabled={docsLoading}
                style={{
                  flex: 1, backgroundColor: '#f5f5f5', border: '1px solid #e5e5e5',
                  borderRadius: 6, padding: '7px 0', fontSize: 11, cursor: 'pointer',
                  fontFamily: 'inherit', color: '#444'
                }}
              >{docsLoading ? 'Loading...' : '\u21BB Refresh'}</button>
              <button
                onClick={indexDocs}
                disabled={indexing || docs.length === 0}
                style={{
                  flex: 1,
                  backgroundColor: indexed ? '#dcfce7' : '#eff6ff',
                  border: `1px solid ${indexed ? '#86efac' : '#bfdbfe'}`,
                  borderRadius: 6, padding: '7px 0', fontSize: 11, cursor: 'pointer',
                  fontFamily: 'inherit', color: indexed ? '#166534' : '#1d4ed8'
                }}
              >{indexing ? 'Indexing...' : indexed ? '\u2713 Indexed' : '\u26A1 Index for Search'}</button>
            </div>

            {indexed && (
              <div style={{ display: 'flex', gap: 6 }}>
                <input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && searchDocs()}
                  placeholder="Semantic search..."
                  style={{
                    flex: 1, border: '1px solid #e5e5e5', borderRadius: 6,
                    padding: '7px 10px', fontSize: 12, outline: 'none', fontFamily: 'inherit'
                  }}
                />
                <button
                  onClick={searchDocs}
                  disabled={searching}
                  style={{
                    backgroundColor: '#2563eb', color: '#fff', border: 'none',
                    borderRadius: 6, padding: '7px 12px', fontSize: 11, cursor: 'pointer',
                    fontFamily: 'inherit', whiteSpace: 'nowrap'
                  }}
                >{searching ? '...' : 'Search'}</button>
                {searchResults.length > 0 && (
                  <button
                    onClick={() => { setSearchResults([]); setSearchQuery(''); }}
                    style={{
                      backgroundColor: '#f5f5f5', border: '1px solid #e5e5e5',
                      borderRadius: 6, padding: '7px 8px', fontSize: 11, cursor: 'pointer',
                      fontFamily: 'inherit', color: '#666'
                    }}
                  >&times;</button>
                )}
              </div>
            )}
          </div>

          {/* Error */}
          {docsError && (
            <div style={{ padding: '8px 16px', backgroundColor: '#fef2f2', color: '#dc2626', fontSize: 12 }}>
              {docsError}
              <button onClick={() => setDocsError('')} style={{ marginLeft: 8, background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontFamily: 'inherit' }}>&times;</button>
            </div>
          )}

          {/* Document list */}
          <div className="ai-chat-scroll" style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
            {docsLoading && docs.length === 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 40 }}>
                <div className="ai-spinner" style={{
                  width: 28, height: 28, border: '3px solid #e5e7eb',
                  borderTopColor: '#2563eb', borderRadius: '50%'
                }} />
                <div style={{ fontSize: 12, color: '#999', marginTop: 10 }}>Loading documents...</div>
              </div>
            )}

            {searchResults.length > 0 && (
              <div style={{ padding: '4px 16px 6px', fontSize: 11, color: '#2563eb', fontWeight: 600 }}>
                {searchResults.length} results for "{searchQuery}"
              </div>
            )}

            {displayDocs.map((doc, i) => (
              <a
                key={doc.id || i}
                href={doc.webUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="ai-doc-row"
                style={{
                  display: 'flex', alignItems: 'flex-start', padding: '8px 16px',
                  textDecoration: 'none', color: 'inherit', gap: 10,
                  borderBottom: '1px solid #f5f5f5'
                }}
              >
                <div style={{ fontSize: 20, lineHeight: 1, marginTop: 2, flexShrink: 0 }}>
                  {fileIcon(doc.type)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: '#1a1a2e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {doc.name}
                  </div>
                  <div style={{ fontSize: 11, color: '#999', marginTop: 2, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {doc.parentFolder && <span>{doc.parentFolder}</span>}
                    {doc.modifiedBy && <span>{doc.modifiedBy}</span>}
                    {doc.lastModified && <span>{new Date(doc.lastModified).toLocaleDateString()}</span>}
                    {doc.size > 0 && <span>{formatSize(doc.size)}</span>}
                    {doc.score != null && <span style={{ color: '#2563eb', fontWeight: 600 }}>{(doc.score * 100).toFixed(0)}% match</span>}
                  </div>
                </div>
              </a>
            ))}

            {!docsLoading && docs.length === 0 && !docsError && (
              <div style={{ textAlign: 'center', padding: '30px 16px', color: '#ccc', fontSize: 13 }}>
                No documents loaded yet
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AIChat;
